const MAX_BYTES = 10 * 1024 * 1024;
const MAX_EDGE = 2000;

const mb = bytes => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

class PhotoSourceError extends Error {}

const viaArrayBuffer = async file => (file.arrayBuffer ? file.arrayBuffer() : null);

const viaFileReader = file => new Promise(resolve => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => resolve(null);
  reader.readAsArrayBuffer(file);
});

const viaSlice = file => viaFileReader(file.slice(0, file.size));

// Android hands Chrome a zero-byte handle for cloud-only Google Photos files on
// the first touch, then materialises it. Try every read path, twice, before
// giving up on the bytes.
const readBytes = async (file, trace) => {
  const strategies = [['arrayBuffer', viaArrayBuffer], ['reader', viaFileReader], ['slice', viaSlice]];
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    for (const [name, strategy] of strategies) {
      try {
        const bytes = await strategy(file);
        if (bytes && bytes.byteLength > 0) { trace.push(`${name}${attempt}:${bytes.byteLength}`); return bytes; }
        trace.push(`${name}${attempt}:empty`);
      } catch (e) { trace.push(`${name}${attempt}:${e.name || 'err'}`); }
    }
    if (attempt === 1) await wait(400);
  }
  return null;
};

const sniff = bytes => {
  const head = new Uint8Array(bytes.slice(0, 16));
  const ascii = String.fromCharCode(...head.subarray(4, 12));
  if (head[0] === 0xff && head[1] === 0xd8) return 'image/jpeg';
  if (head[0] === 0x89 && head[1] === 0x50) return 'image/png';
  if (ascii.startsWith('ftyp')) {
    const brand = ascii.slice(4, 8);
    if (brand.startsWith('hei') || brand.startsWith('mif') || brand.startsWith('msf')) return 'image/heic';
    if (brand.startsWith('avi')) return 'image/avif';
  }
  if (String.fromCharCode(...head.subarray(0, 4)) === 'RIFF') return 'image/webp';
  return null;
};

const decodeBlob = async (blob, trace, tag) => {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(blob); } catch (e) { trace.push(`${tag}-bitmap:${e.name || 'fail'}`); }
  }
  const url = URL.createObjectURL(blob);
  const image = new Image();
  image.src = url;
  try {
    if (image.decode) await image.decode();
    else await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = () => reject(new Error('load')); });
    return image;
  } catch (e) {
    trace.push(`${tag}-img:${e.name || 'fail'}`);
    return null;
  } finally { setTimeout(() => URL.revokeObjectURL(url), 0); }
};

// HEIC/HEIF is the default camera format on iPhones and some Android phones and
// no browser can decode it natively, so it is transcoded with libheif in-page.
const decodeHeic = async (blob, trace) => {
  try {
    const { heicTo } = await import('heic-to');
    const jpeg = await heicTo({ blob, type: 'image/jpeg', quality: 0.92 });
    return await decodeBlob(jpeg, trace, 'heic');
  } catch (e) { trace.push(`heic:${e.name || 'fail'}`); return null; }
};

// Returns a scaled JPEG working copy for the editor plus the file to upload.
// Byte-level reads are attempted first; if the OS never yields bytes we decode
// the file handle directly and rebuild a full-resolution JPEG from the pixels,
// so a photo that is visible in the editor can always be saved.
export const prepareEditorSource = async file => {
  if (file.size > MAX_BYTES) throw new PhotoSourceError(`Photos must be 10 MB or smaller. This one is ${mb(file.size)}.`);
  const trace = [`type:${file.type || 'none'}`, `size:${file.size}`];

  const bytes = await readBytes(file, trace);
  const blocked = trace.some(entry => entry.includes('NotReadableError'));
  let bitmap = null;
  if (bytes) {
    const actual = sniff(bytes);
    trace.push(`sniff:${actual || 'unknown'}`);
    const blob = new Blob([bytes], { type: actual || file.type || 'image/jpeg' });
    if (actual === 'image/heic') bitmap = await decodeHeic(blob, trace);
    if (!bitmap) bitmap = await decodeBlob(blob, trace, 'bytes');
    if (!bitmap && actual !== 'image/heic') bitmap = await decodeHeic(blob, trace);
  }
  if (!bitmap) bitmap = await decodeBlob(file, trace, 'file');
  if (!bitmap && !bytes) bitmap = await decodeHeic(file, trace);

  const width = bitmap && (bitmap.width || bitmap.naturalWidth);
  const height = bitmap && (bitmap.height || bitmap.naturalHeight);
  if (!bitmap || !width || !height) {
    const error = new PhotoSourceError(blocked
      ? 'This window is not allowed to read files from your gallery.'
      : "This photo couldn't be reached just now. Tap Upload photo and pick it again.");
    error.detail = trace.join(' · ');
    error.blocked = blocked;
    throw error;
  }

  const draw = (w, h) => {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    return canvas;
  };

  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const working = draw(Math.round(width * scale), Math.round(height * scale));
  const source = working.toDataURL('image/jpeg', 0.92);
  working.width = 0; working.height = 0;

  // Keep the untouched file only when its bytes were readable and the server
  // already stores that format. Otherwise upload pixels we know we have.
  const usable = bytes && sniff(bytes) === file.type && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  // Never hand the OS file handle to the uploader: Android in-app windows can
  // revoke access between picking and saving. Upload the exact bytes we hold.
  let uploadFile = usable ? new File([bytes], file.name || 'photo.jpg', { type: file.type }) : file;
  if (!usable) {
    // Mobile browsers cannot allocate a 24 MP canvas; rebuild at a bounded size
    // and step down if the browser refuses to encode it.
    let converted = null;
    for (const edge of [4000, 3000, 2000]) {
      const s = Math.min(1, edge / Math.max(width, height));
      const full = draw(Math.round(width * s), Math.round(height * s));
      try { converted = await new Promise(resolve => full.toBlob(resolve, 'image/jpeg', 0.9)); } catch { converted = null; }
      full.width = 0; full.height = 0;
      if (converted && converted.size > 0 && converted.size <= MAX_BYTES) break;
      converted = null;
    }
    if (!converted) throw new PhotoSourceError("This photo couldn't be prepared. Tap Upload photo and pick it again.");
    uploadFile = new File([converted], `${(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' });
  }
  if (bitmap.close) bitmap.close();
  return { source, uploadFile };
};

export const BLOCKED_FILE_MESSAGE = "This preview window can't access that file. Open the site in Chrome (↗ above) or use Take photo / Record video.";

// Wraps a network-level failure (no HTTP response) as a blocked-file error.
export const blockedFailure = cause => {
  const error = new PhotoSourceError(BLOCKED_FILE_MESSAGE);
  error.blocked = true;
  error.detail = cause?.message || 'network error';
  return error;
};

export const photoSourceMessage = error => {
  if (!(error instanceof PhotoSourceError)) return "This photo couldn't be reached just now. Tap Upload photo and pick it again.";
  return error.message;
};
export const photoSourceDetail = error => (error instanceof PhotoSourceError ? error.detail || '' : '');
